import { z } from 'zod'

import { httpClient, ReadService, type HttpClient } from '../core'
import { templateVersionsEndpoints } from './template-versions.endpoints'
import { createTemplateVersionResponseSchema, templateVersionPageSchema, templateVersionSchema, TEMPLATE_VERSION_NAME_MAX, type GenerateVariationBody, type CreateTemplateVersionResponseDto, type TemplateVersionDto, type TemplateVersionFilters, type TemplateVersionUpdateBody } from './template-versions.schemas'

/** Reads a project's template versions (`GET /api/v1/template-versions?projectId=…`) and makes new ones and starts new ones (`POST /api/projects/{id}/templates/generate-variations`). */
export class TemplateVersionsService extends ReadService<TemplateVersionDto, TemplateVersionFilters> {
  constructor(http: HttpClient = httpClient) {
    super(http, templateVersionsEndpoints, { item: templateVersionSchema, page: templateVersionPageSchema })
  }

  /**
   * Starts one variation of an approved template. The server stores it as a draft with the creator, the content and the instructions,
   * queues the task that draws it, and answers with both: follow the task through its event stream. Never retried: a lost request may
   * still have worked, and a second one would make a second variation.
   */
  generate(projectId: string, body: GenerateVariationBody, signal?: AbortSignal): Promise<CreateTemplateVersionResponseDto> {
    const instructions = body.instructions?.trim()
    const name = body.name?.trim().slice(0, TEMPLATE_VERSION_NAME_MAX)
    return this.http.post(templateVersionsEndpoints.generateVariations(projectId), createTemplateVersionResponseSchema, { body: { createdBy: body.createdBy, ...(name && { name }), content: body.content, imageUrl: body.imageUrl, ...(instructions && { instructions }) }, signal, timeoutMs: 2 * 60_000 })
  }

  /** Changes a variation (`PUT /api/v1/template-versions/{id}`): its name, sharing it for review, or the client's approval with the SKU. Not retried. */
  update(id: string, body: TemplateVersionUpdateBody, signal?: AbortSignal): Promise<TemplateVersionDto> {
    const name = body.name?.trim().slice(0, TEMPLATE_VERSION_NAME_MAX)
    const sku = body.sku?.trim()
    return this.http.put(templateVersionsEndpoints.item(id), templateVersionSchema, { body: { ...body, ...(name !== undefined && { name }), ...(sku !== undefined && { sku }) }, signal })
  }

  /** Deletes a variation (`DELETE /api/v1/template-versions/{id}`); the server answers with no body. */
  async remove(id: string, signal?: AbortSignal): Promise<void> {
    await this.http.delete(templateVersionsEndpoints.item(id), z.null(), { signal })
  }

  /** Every variation of one project, newest first. */
  async ofProject(projectId: string, filters: Omit<TemplateVersionFilters, 'projectId'> = {}, signal?: AbortSignal): Promise<TemplateVersionDto[]> {
    const items = await this.listAll({ ...filters, projectId }, signal)
    return items.toSorted((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  }
}

export const templateVersionsService = new TemplateVersionsService()
